import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'next-i18next';
import Tooltip from 'react-tooltip-lite';
import {
  formatNumericValue,
  getDecimalPlacesValidation,
  nameIdPairValidationSchema,
  truncateString,
} from 'utils/general';
import { themeColors } from 'utils/tailwind/tailwindHelpers';
import * as yup from 'yup';

import { IWarehouseManagement } from '@/modules/warehouseManagement/shared/types';
import CreateEditInboundDelivery from '@/modules/warehouseManagement/warehouses/components/InboundDelivery/CreateEditInboundDelivery';
import InboundDeliveryGeneralInfo from '@/modules/warehouseManagement/warehouses/components/InboundDelivery/CreateEditInboundDelivery/Steps/GeneralInformation';
import InboundDeliveryInputs from '@/modules/warehouseManagement/warehouses/components/InboundDelivery/CreateEditInboundDelivery/Steps/Inputs';
import Review from '@/modules/warehouseManagement/warehouses/components/InboundDelivery/CreateEditInboundDelivery/Steps/Review';
import { Typography } from '@/shared/components/atoms';
import { IIconWithTitleDescriptionProps } from '@/shared/components/atoms/IconWithTItleDescription/IconWithTitleDescription';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { IMultiStepFormStep } from '@/shared/components/molecules/MultiStepForm/IMultiStepForm';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { getUnitName } from '@/shared/lib/convert-units';
import { IGeneral } from '@/shared/types';

import CalendarIcon from '~/images/shared/calender.svg';
import InfoCircle from '~/images/shared/ExleminationMark.svg';
import SupplierIcon from '~/images/shared/GrassWithHand.svg';
import NoteIcon from '~/images/shared/note.svg';
import HouseIcon from '~/images/shared/Packhouse.svg';

export const useCreateInboundDeliveryUtils = () => {
  const { t } = useTranslation();

  const [selectedAsset] = useStore((store) => store?.asset);

  const { openModal } = useModal();

  const openCreateInboundDeliveryModal = (editId?: number) =>
    openModal(<CreateEditInboundDelivery editId={editId} />, {
      variant: 'full',
      title: t(editId ? 'edit-inbound-delivery' : 'add-new-stock'),
      titleVariant: 'h2',
      onClose: () => {
        openModal(
          <ConfirmationModal
            header={t('going-somewhere-else')}
            message={t('data-loss-warning')}
            variant='warning'
            dismissText={t('continue-editing')}
            confirmText={t('discard-and-exit')}
            onConfirmRemoveNumber={3}
            buttonsAlignment='col'
            preferredAction='dismiss'
          />
        );
      },
    });

  const steps: IMultiStepFormStep<IWarehouseManagement.ICreateInboundDeliveryFormValues>[] =
    [
      {
        title: t('general-information'),
        render: () => <InboundDeliveryGeneralInfo />,
        fields: [
          'warehouse.id',
          'supplier.id',
          'supplyOrder',
          'depositOrderDate',
        ],
      },
      {
        title: t('Inputs'),
        render: () => <InboundDeliveryInputs />,
        fields: ['products'],
      },
      {
        title: t('review'),
        render: ({ setStep }) => <Review setStep={setStep} />,
        fields: [],
      },
    ];

  const CreateEditInboundDeliverySchema: yup.SchemaOf<IWarehouseManagement.ICreateInboundDeliveryFormValues> =
    yup.object().shape({
      warehouse: nameIdPairValidationSchema(t),
      supplier: nameIdPairValidationSchema(t),
      supplyOrder: yup
        .string()
        .max(
          50,
          t('this-field-not-accept-more-than-x-character', { count: 50 })
        )
        .notRequired(),
      depositOrderDate: yup.string().required(t('this-field-is-required')),
      // must be at least one product
      products: yup
        .array()
        .of(
          yup.object().shape({
            product: nameIdPairValidationSchema(t)
              .shape({
                measurementType: yup
                  .mixed()
                  .oneOf(['WEIGHT', 'VOLUME', 'SEEDS'])
                  .required(),
              })
              .nullable(),
            batchId: yup.number().notRequired(),
            status: yup
              .mixed()
              .oneOf(['used', 'unused', 'partiallyUsed'])
              .notRequired(),
            categoryId: yup
              .number()
              .required(t('this-field-is-required'))
              .nullable(),
            countOfPackages: getDecimalPlacesValidation(
              t('this-field-accepts-two-decimal-places'),
              2
            )
              .moreThan(0, t('This field must be greater than zero.'))
              .required(t('this-field-is-required'))
              .nullable(),
            packageSize: getDecimalPlacesValidation(
              t('this-field-accepts-five-decimal-places'),
              5
            )
              .moreThan(0, t('This field must be greater than zero.'))
              .required(t('this-field-is-required'))
              .nullable(),
            packagePrice: getDecimalPlacesValidation(
              t('this-field-accepts-three-decimal-places'),
              3
            )
              .min(0, t('field-greater-than-val', { val: 0 }))
              .required(t('this-field-is-required'))
              .nullable(),
            packageUnit: yup
              .string()
              .required(t('this-field-is-required'))
              .nullable(),
            productionDate: yup.string().when('product.measurementType', {
              is: 'SEEDS',
              then: yup.string().notRequired().nullable(),
              otherwise: yup
                .string()
                .required(t('this-field-is-required'))
                .nullable(),
            }),
            expiryDate: yup
              .string()
              .when(['product.measurementType', 'productionDate'], {
                is: (
                  measurementType: IGeneral.IMeasurementType,
                  productionDate: string
                ) => measurementType === 'SEEDS' && !productionDate,
                then: yup.string().notRequired().nullable(),
                otherwise: yup
                  .string()
                  .required(t('this-field-is-required'))
                  .nullable(),
              }),
          })
        )
        .min(1, 'non-zero-products')
        .required('this-field-is-required'),
      editId: yup.number().notRequired(),
    });

  const sharedCardProps = {
    titleTypography: {
      variant: 'caption' as const,
    },
    descriptionTypography: {
      weight: 'medium' as const,
    },
    iconGap: 'gap-16',
    iconAlignment: 'center' as const,
  };

  const getReviewGeneralCardItems = (
    formValues: Omit<
      IWarehouseManagement.ICreateInboundDeliveryFormValues,
      'products'
    >
  ): IIconWithTitleDescriptionProps[] => [
    {
      ...sharedCardProps,
      title: t('warehouse'),
      description: formValues.warehouse.name,
      icon: <HouseIcon width='24' height='24' />,
    },
    {
      ...sharedCardProps,
      title: t('the-supplier'),
      description: formValues.supplier.name,
      icon: <SupplierIcon width='24' height='24' />,
    },
    {
      ...sharedCardProps,
      title: t('supply-order-number'),
      description: formValues?.supplyOrder ? (
        <span title={formValues.supplyOrder}>
          {truncateString({ text: formValues.supplyOrder, length: 20 })}
        </span>
      ) : (
        '-'
      ),
      icon: <NoteIcon width='24' height='24' />,
    },
    {
      ...sharedCardProps,
      title: t('inbound-delivery-date'),
      description: formValues.depositOrderDate,
      icon: <CalendarIcon width='24' height='24' />,
    },
  ];

  const getReviewInputsTable = () => {
    const columnHelper =
      createColumnHelper<IWarehouseManagement.IInboundDeliveryFormProduct>();
    return [
      columnHelper.accessor('product', {
        header: t('inputs'),
        cell: ({ getValue }) => (
          <div className='flex items-center gap-8'>
            <Typography>{getValue()?.name}</Typography>
            {getValue()?.activeSubstances?.length && (
              <Tooltip
                content={
                  <div className='flex w-fit min-w-[100px] flex-col px-16 py-8'>
                    {getValue()?.activeSubstances.map((substance) => (
                      <Typography key={substance.id}>
                        {`${substance.name} ${substance.concentration}%`}
                      </Typography>
                    ))}
                  </div>
                }
                background={themeColors().background['neutral-99']}
                className='react-tooltip-rounded'
                arrow
                arrowSize={10}
                direction='bottom'
                tagName='div'
                tipContentHover
                padding={0}
              >
                <span className='cursor-pointer text-primary-default'>
                  <InfoCircle width='16' height='16' />
                </span>
              </Tooltip>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('countOfPackages', {
        header: t('no-of-packages'),
        cell: ({ getValue }) => formatNumericValue(getValue() as number),
      }),
      columnHelper.accessor('packagePrice', {
        header: `${t('package-price')} (${t(selectedAsset?.currency ?? '')})`,
        cell: ({ getValue }) => formatNumericValue(getValue() as number),
      }),
      columnHelper.accessor('packageSize', {
        header: t('weight-per-package'),
        cell: ({ getValue }) => formatNumericValue(getValue() as number),
      }),
      columnHelper.accessor('packageUnit', {
        header: t('measurement-unit'),
        cell: ({ getValue }) => t(getUnitName(getValue() ?? undefined)),
      }),
      columnHelper.accessor('productionDate', {
        header: t('production-date'),
        cell: ({ getValue }) => getValue() ?? '-',
      }),
      columnHelper.accessor('expiryDate', {
        header: t('expiry-date'),
        cell: ({ getValue }) => getValue() ?? '-',
      }),
    ];
  };

  const getMutationObject = (
    formValues: IWarehouseManagement.ICreateInboundDeliveryFormValues
  ): IWarehouseManagement.ICreateInboundDeliverySubmissionSchema => {
    return {
      warehouseId: formValues.warehouse.id,
      supplierId: formValues.supplier.id,
      supplyOrder: formValues.supplyOrder,
      depositOrderDate: formValues.depositOrderDate,
      products: formValues.products.map(
        (item) =>
          ({
            productId: item.product?.id,
            countOfPackages: item.countOfPackages,
            packageSize: item.packageSize,
            packagePrice: item.packagePrice,
            packageUnit: item.packageUnit,
            productionDate: item.productionDate,
            expiryDate: item.expiryDate,
            measurementType: item.product?.measurementType,
          } as IWarehouseManagement.IWarehouseProductSchema)
      ),
    };
  };

  const getDefaultValues = (
    data: IWarehouseManagement.IInboundDeliveryGeneralInfo & {
      products: IWarehouseManagement.IInboundDeliveryBatch[];
    }
  ) => ({
    warehouse: {
      name: data.warehouseName,
      id: data.warehouseId,
    },
    supplier: {
      name: data.supplier,
      id: data.supplierId,
    },
    supplyOrder: data.supplyOrder,
    depositOrderDate: data.depositOrderDate,
    products: data.products.map((input) => ({
      product: {
        name: input.productName,
        id: input.productId,
        measurementType: input.productMeasurementType,
        activeSubstances: input?.activeSubstances?.map((item) => ({
          ...item,
          name: item.activeSubstanceName,
        })),
      },
      categoryId: input.categoryId,
      batchId: input.batchId,
      status: input.status,
      countOfPackages: input.countOfPackages,
      packageSize: input.packageSize,
      packagePrice: input.packagePrice,
      packageUnit: input.packageUnit,
      productionDate: input.productionDate,
      expiryDate: input.expiryDate,
    })),
  });

  const compareChanges = <T,>(oldValue: T, newValue: T) => {
    return oldValue === newValue ? undefined : newValue ?? undefined;
  };

  const getBatchDifferences = (
    defaultInput: IWarehouseManagement.IInboundDeliveryBatch,
    formInput: IWarehouseManagement.IInboundDeliveryFormProduct
  ): IWarehouseManagement.IEditInboundDeliveryProduct | undefined => {
    const obj = {
      productId: compareChanges(defaultInput.productId, formInput.product?.id),
      countOfPackages: compareChanges(
        defaultInput.countOfPackages,
        formInput.countOfPackages
      ),
      packageSize: compareChanges(
        defaultInput.packageSize,
        formInput.packageSize
      ),
      packagePrice: compareChanges(
        defaultInput.packagePrice,
        formInput.packagePrice
      ),
      packageUnit: compareChanges(
        defaultInput.packageUnit,
        formInput.packageUnit
      ),
      productionDate: compareChanges(
        defaultInput.productionDate,
        formInput.productionDate
      ),
      expiryDate: compareChanges(defaultInput.expiryDate, formInput.expiryDate),
    };

    return Object.values(obj).filter((value) => Boolean(value)).length
      ? { ...obj, operation: 'edit', batchId: defaultInput.batchId }
      : undefined;
  };

  const getBatchesUpdates = (
    defaultInputs: IWarehouseManagement.IInboundDeliveryBatch[],
    formInputs: IWarehouseManagement.IInboundDeliveryFormProduct[]
  ) => {
    const batches: IWarehouseManagement.IEditInboundDeliveryProduct[] = [];
    defaultInputs.forEach((defaultBatch) => {
      const formBatch = formInputs.find(
        (batch) => batch.batchId === defaultBatch.batchId
      );
      if (!formBatch)
        batches.push({
          operation: 'remove',
          batchId: defaultBatch.batchId,
        });
      else {
        const newBatch = getBatchDifferences(defaultBatch, formBatch);
        if (newBatch) batches.push(newBatch);
      }
    });

    return batches.length ? batches : undefined;
  };

  // To have a log of the updates made, we need to include only the changed properties. For the products array, we need to keep track of updated and deleted entries.
  const getEditMutation = (
    formValues: IWarehouseManagement.ICreateInboundDeliveryFormValues,
    defaultValues: IWarehouseManagement.IInboundDeliveryGeneralInfo & {
      products: IWarehouseManagement.IInboundDeliveryBatch[];
    }
  ): IWarehouseManagement.IEditInboundDeliverySubmissionSchema => ({
    supplierId: compareChanges(
      defaultValues.supplierId,
      formValues.supplier.id
    ),
    supplyOrder: formValues?.supplyOrder
      ? compareChanges(defaultValues.supplyOrder, formValues.supplyOrder)
      : null,
    depositOrderDate: compareChanges(
      defaultValues.depositOrderDate,
      formValues.depositOrderDate
    ),
    products: getBatchesUpdates(defaultValues.products, formValues.products),
  });

  return {
    openCreateInboundDeliveryModal,
    steps,
    CreateEditInboundDeliverySchema,
    getReviewGeneralCardItems,
    getReviewInputsTable,
    getMutationObject,
    getEditMutation,
    getDefaultValues,
  };
};
